import React, { useCallback, useState, useEffect } from "react";
import { PresalesListTableRow } from "./PresalesListTableRow";
import { SortableHeaderText } from "../../Components/Table/SortableHeaderText";
import { DivCell } from "./DivCell";
import { Virtuoso } from "react-virtuoso";
import { getColumnsForConfiguration, isColumnVisible } from "./PresalesTableConfig";
import { useCarListParameters } from "../../Hooks/useCarListParameters";
import { useDisclosure } from "@mantine/hooks";
import { Drawer } from "@mantine/core";
import { DirectionEnum } from "../../Enums/DirectionEnum";
import { ShowroomCarView } from "../Showroom/ShowroomCarView";

export const PresalesListTable = React.memo(({ cars, useNaturalScrolling = false, hidePrices = false, equipmentIcons, activeCarId, activeSortField, activeSortAscending, fnSetSortByField, fnSetSortByFieldOrder, fnUpdateCarList, tableConfiguration = 'full' }) => {
    const columns = getColumnsForConfiguration(tableConfiguration, hidePrices);
    const [activeCar, setActiveCar] = useState();
    const { setCarListParameters } = useCarListParameters();
    const [opened, { open, close }] = useDisclosure(false);
    const [currentCarIndex, setCurrentCarIndex] = useState(null);

    const handleCarClicked = (car) => {
        if (car) {
            setCarListParameters({ carhash: car.hash });
            setActiveCar(car);
        } else {
            setCarListParameters({ carhash: null });
            setActiveCar(null);
        }
    };

    useEffect(() => {
        if (activeCar && cars?.length) {
            const index = cars.findIndex(car => car.hash === activeCar.hash);
            setCurrentCarIndex(index);
            open();
        } else {
            close();
        }
    }, [activeCar]);

    const changeCar = useCallback((direction) => {
        if (cars && currentCarIndex !== null && opened) {
            const count = cars.length;
            const newIndex =
                direction === DirectionEnum.next
                    ? (currentCarIndex + 1) % count
                    : (currentCarIndex - 1 + count) % count;

            handleCarClicked(cars[newIndex]);
        }
    }, [currentCarIndex, cars, opened, handleCarClicked]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handleKeyDown = useCallback((e) => {
        if (!opened || currentCarIndex === null) return;

        if (e.key === 'ArrowRight') {
            changeCar(DirectionEnum.next);
        } else if (e.key === 'ArrowLeft') {
            changeCar(DirectionEnum.previous);
        }
    }, [opened, currentCarIndex, cars]);

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleKeyDown]);

    return (
        <div>
            <div className="sticky top-0 z-10">
                <div className="text-sm flex items-center border-l-[5px] border-l-background-color font-semibold bg-background-color px-2 py-4 mb-2">
                    {columns.map((column) => {
                        if (!isColumnVisible(column, hidePrices)) return null;

                        const cellClassName = `${column.width} ${column.responsiveClass || ''}`;
                        const conditionalClassName = column.conditionalVisibility === 'hidePrices' && hidePrices ? 'hidden' : '';
                        const finalClassName = `${cellClassName} ${conditionalClassName}`.trim();

                        return (
                            <DivCell key={column.key} className={finalClassName}>
                                <SortableHeaderText
                                    title={column.title}
                                    activeSortAscending={activeSortAscending}
                                    activeSortField={activeSortField}
                                    className={column.headerClassName || ''}
                                    textAlignment={column.textAlignment || ''}
                                    setSortFieldValue={fnSetSortByField}
                                    setSortFieldOrder={fnSetSortByFieldOrder}
                                    sortFieldName={column.sortField}
                                    sortAscendingDefault={column.sortAscendingDefault}
                                />
                            </DivCell>
                        );
                    })}
                </div>
            </div>

            <div>
                {!useNaturalScrolling &&
                    <Virtuoso
                        style={{ height: 'calc(100vh - 155px)' }}
                        data={cars}
                        itemContent={(index, car) => (
                            <PresalesListTableRow key={car.car_id} car={car} hidePrices={hidePrices} equipmentIcons={equipmentIcons} activeCarId={activeCarId} fnUpdateCarList={fnUpdateCarList} fnSetActiveCar={handleCarClicked} columns={columns} />
                        )}
                    />
                }
                {useNaturalScrolling &&
                    <div>
                        {cars.map((car) => (
                            <PresalesListTableRow key={car.car_id} car={car} hidePrices={hidePrices} equipmentIcons={equipmentIcons} activeCarId={activeCarId} fnUpdateCarList={fnUpdateCarList} fnSetActiveCar={handleCarClicked} columns={columns} />
                        ))}
                    </div>
                }
            </div>
            <Drawer
                position="right"
                size="1100px"
                padding="md"
                opened={opened}
                onClose={() => {
                    close();
                    handleCarClicked(null);
                }}
                withOverlay={true}
                overlayProps={{
                    style: { backgroundColor: 'rgba(0, 0, 0, 0.2)' }
                }}
                withCloseButton={false}
                lockScroll={true}
                keepMounted={true}
            >
                <ShowroomCarView car={activeCar} changeCar={changeCar} hidePrices={hidePrices} close={() => { close(); handleCarClicked(null); }} equipmentBaseIcons={equipmentIcons} fnUpdateData={fnUpdateCarList} />
            </Drawer>
        </div>
    )
},
    (prevProps, nextProps) => {
        // Custom comparison logic
        return (
            prevProps.cars === nextProps.cars
        );
    })
